import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MainNavigation } from '../main/MainNavigation';
import { 
  ChefHat,
  Target,
  ShoppingCart,
  Heart,
  GraduationCap,
  Home,
  Apple,
  Dumbbell,
  Star,
  Shield,
  CheckCircle,
  Smartphone,
  Bookmark
} from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';

export function WhyMenioo() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const redirectToApp = useAuthStore((state) => state.redirectToApp);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSignupRedirect = (e: React.MouseEvent) => {
    e.preventDefault();
    redirectToApp('/signup');
  };

  const handleAppRedirect = (e: React.MouseEvent) => {
    e.preventDefault();
    redirectToApp('/');
  };

  const ctaButton = (text: string, className: string) => {
    if (isAuthenticated) {
      return (
        <a 
          href="https://app.menioo.com" 
          onClick={handleAppRedirect}
          className={className}
        >
          {text}
        </a>
      );
    }
    return (
      <a 
        href="https://app.menioo.com/signup"
        onClick={handleSignupRedirect}
        className={className}
      >
        {text}
      </a>
    );
  };

  return (
    <div className="min-h-screen bg-menioo-cream">
      <MainNavigation />

      {/* 1. HERO */}
      <section className="relative bg-gradient-to-br from-menioo-cream via-white to-menioo-cream py-16 md:py-24 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-menioo-green/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-menioo-orange/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center bg-menioo-green/10 text-menioo-green-dark px-4 py-2 rounded-full text-sm font-medium">
                <Heart className="h-4 w-4 mr-2" />
                Notre mission
              </div>
              
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-menioo-navy leading-tight">
                  Pourquoi <span className="text-menioo-green">Menioo&nbsp;?</span>
                </h1>
                <p className="text-lg md:text-xl text-gray-700 max-w-lg leading-relaxed">
                  Parce que vous méritez de <strong>cuisiner les recettes qui vous font envie</strong> — qu'elles viennent de la communauté, de vos créateurs préférés ou de votre imagination. Menioo transforme l'inspiration en action.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                {ctaButton(
                  'Commencer gratuitement',
                  'inline-flex items-center justify-center bg-menioo-green text-white px-8 py-4 rounded-xl text-center font-semibold text-lg hover:bg-menioo-green-dark transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5'
                )}
              </div>
            </div>
            
            <div className="hidden lg:block relative">
              <div className="absolute -top-6 -left-6 w-28 h-28 bg-menioo-orange/15 rounded-full animate-bounce-subtle"></div>
              <div className="absolute -bottom-10 -right-10 w-36 h-36 bg-menioo-green/15 rounded-full animate-bounce-subtle" style={{ animationDelay: '1s' }}></div>
              <div className="bg-white rounded-2xl shadow-2xl p-4 max-w-md mx-auto">
                <img 
                  src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
                  alt="Cuisiner avec plaisir grâce à Menioo" 
                  loading="lazy"
                  className="rounded-xl w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. LE CONSTAT */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy">
                  L'inspiration ne manque pas. L'organisation, si.
                </h2>
                
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Les idées de recettes sont partout : sur les réseaux sociaux, dans les livres, dans les discussions entre amis. <strong>L'inspiration ne manque pas</strong>.
                  </p>
                  <p>
                    Ce qui manque, c'est un <strong>système simple</strong> pour passer de l'idée au plat sur la table. Sans prise de tête, sans oubli en courses, sans budget qui explose.
                  </p>
                  <p className="text-lg font-medium text-menioo-navy">
                    Le problème n'est pas le manque d'idées. C'est le passage à l'action.
                  </p>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-8 border border-gray-100">
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mr-4 flex-shrink-0">
                      <Smartphone className="h-6 w-6 text-red-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-menioo-navy">5x par semaine</p>
                      <p className="text-sm text-gray-600">"On mange quoi ce soir ?" sans réponse</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mr-4 flex-shrink-0">
                      <Bookmark className="h-6 w-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-menioo-navy">Des dizaines de recettes</p>
                      <p className="text-sm text-gray-600">sauvegardées partout, jamais cuisinées</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mr-4 flex-shrink-0">
                      <ShoppingCart className="h-6 w-6 text-purple-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-menioo-navy">240€ / mois</p>
                      <p className="text-sm text-gray-600">en livraisons, restos et gaspillage</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. LA SOLUTION */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-menioo-cream/30 to-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy mb-4">
              Menioo, c'est le <span className="text-menioo-green">chaînon manquant</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Une communauté de recettes, un planning intelligent et une liste de courses automatique — tout ce qu'il faut pour cuisiner sans prise de tête.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-green/10 to-menioo-green/20 flex items-center justify-center mb-5">
                <Target className="h-8 w-8 text-menioo-green" />
              </div>
              <h3 className="text-xl font-bold text-menioo-navy mb-3">De l'intention à l'action</h3>
              <p className="text-gray-600 leading-relaxed">
                Fini le "je le ferai un jour". Trouvez une recette dans la communauté ou importez-la, planifiez-la et Menioo génère la liste de courses. Le passage à l'action devient automatique.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-orange/10 to-menioo-orange/20 flex items-center justify-center mb-5">
                <ShoppingCart className="h-8 w-8 text-menioo-orange" />
              </div>
              <h3 className="text-xl font-bold text-menioo-navy mb-3">Zéro charge mentale</h3>
              <p className="text-gray-600 leading-relaxed">
                Plus besoin de réfléchir à quoi acheter, en quelle quantité, dans quel rayon. Menioo calcule tout et génère votre liste de courses parfaite.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-green/10 to-menioo-green/20 flex items-center justify-center mb-5">
                <CheckCircle className="h-8 w-8 text-menioo-green" />
              </div>
              <h3 className="text-xl font-bold text-menioo-navy mb-3">Vos objectifs respectés</h3>
              <p className="text-gray-600 leading-relaxed">
                Veggie, moins de viande, plus de légumes, budget maîtrisé. Définissez vos objectifs et Menioo vous aide à les tenir naturellement.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-orange/10 to-menioo-orange/20 flex items-center justify-center mb-5">
                <Heart className="h-8 w-8 text-menioo-orange" />
              </div>
              <h3 className="text-xl font-bold text-menioo-navy mb-3">La fierté du fait-maison</h3>
              <p className="text-gray-600 leading-relaxed">
                L'effet IKEA : quand on fait soi-même, c'est meilleur. Retrouvez la fierté de cuisiner et le plaisir de partager un vrai repas avec vos proches.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. POUR QUI ? */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy mb-4">
              Menioo, c'est fait pour vous si...
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Que vous soyez débutant ou passionné, Menioo s'adapte à votre vie.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-8 text-center hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-green/10 to-menioo-green/20 flex items-center justify-center mx-auto mb-5">
                <GraduationCap className="h-8 w-8 text-menioo-green" />
              </div>
              <h3 className="text-lg font-bold text-menioo-navy mb-3">Étudiants & jeunes actifs</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Petit budget, peu de temps, mais envie de mieux manger que des pâtes et du Uber Eats. Menioo vous guide pour cuisiner simple et pas cher.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-8 text-center hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-orange/10 to-menioo-orange/20 flex items-center justify-center mx-auto mb-5">
                <Home className="h-8 w-8 text-menioo-orange" />
              </div>
              <h3 className="text-lg font-bold text-menioo-navy mb-3">Familles pressées</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Organisation pour toute la famille, partage des menus et des courses, enfants qui participent. Zéro stress, zéro oubli.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-8 text-center hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-green/10 to-menioo-green/20 flex items-center justify-center mx-auto mb-5">
                <Apple className="h-8 w-8 text-menioo-green" />
              </div>
              <h3 className="text-lg font-bold text-menioo-navy mb-3">Amateurs de food content</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Vous adorez découvrir des recettes partout ? Avec Menioo, passez de l'inspiration à la cuisine. Communauté, TikTok, Instagram — tout finit dans votre assiette.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-8 text-center hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-orange/10 to-menioo-orange/20 flex items-center justify-center mx-auto mb-5">
                <Dumbbell className="h-8 w-8 text-menioo-orange" />
              </div>
              <h3 className="text-lg font-bold text-menioo-navy mb-3">Sport & objectifs santé</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Équilibre nutritionnel, batch cooking, objectifs alimentaires précis. Menioo vous aide à manger en accord avec vos ambitions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. TÉMOIGNAGES */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-menioo-cream/30 to-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy mb-4">
              La communauté témoigne
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ils ont rejoint Menioo et transformé leur quotidien en cuisine.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            <div className="text-center">
              <div className="text-5xl font-bold text-menioo-green mb-2">+42€</div>
              <p className="text-gray-600 font-medium">économisés par semaine</p>
              <p className="text-sm text-gray-400">en moyenne par foyer</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-menioo-orange mb-2">2h</div>
              <p className="text-gray-600 font-medium">gagnées par semaine</p>
              <p className="text-sm text-gray-400">de charge mentale en moins</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-menioo-green mb-2">4x</div>
              <p className="text-gray-600 font-medium">plus de repas faits maison</p>
              <p className="text-sm text-gray-400">dès la première semaine</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <div className="flex items-center mb-4">
                {[1,2,3,4,5].map((star) => (
                  <Star key={star} className="h-5 w-5 text-menioo-orange fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic leading-relaxed">
                "J'adore découvrir les recettes de la communauté. Ça m'a donné envie de cuisiner — maintenant j'en fais 4 par semaine. C'est devenu un jeu !"
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-menioo-green/20 flex items-center justify-center mr-3">
                  <span className="font-bold text-menioo-green">M</span>
                </div>
                <div>
                  <p className="font-semibold text-menioo-navy">Marie, 27 ans</p>
                  <p className="text-sm text-gray-500">Community manager</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <div className="flex items-center mb-4">
                {[1,2,3,4,5].map((star) => (
                  <Star key={star} className="h-5 w-5 text-menioo-orange fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic leading-relaxed">
                "Mes objectifs veggie sont enfin tenus ! Plus d'improvisation viande au dernier moment. Les enfants adorent choisir leurs recettes."
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-menioo-orange/20 flex items-center justify-center mr-3">
                  <span className="font-bold text-menioo-orange">T</span>
                </div>
                <div>
                  <p className="font-semibold text-menioo-navy">Thomas, 41 ans</p>
                  <p className="text-sm text-gray-500">Papa de 3 enfants</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <div className="flex items-center mb-4">
                {[1,2,3,4,5].map((star) => (
                  <Star key={star} className="h-5 w-5 text-menioo-orange fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic leading-relaxed">
                "En coloc, c'était l'enfer pour les courses. Maintenant on partage le planning et la liste. Zéro conflit, zéro oubli !"
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-menioo-green/20 flex items-center justify-center mr-3">
                  <span className="font-bold text-menioo-green">L</span>
                </div>
                <div>
                  <p className="font-semibold text-menioo-navy">Laura, 24 ans</p>
                  <p className="text-sm text-gray-500">Étudiante en coloc</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-center">
            <div className="flex items-center bg-white rounded-full px-6 py-3 shadow-sm border border-gray-100">
              <Shield className="h-6 w-6 text-menioo-green mr-3" />
              <span className="text-sm font-medium text-menioo-navy">
                Connexion Google sécurisée &bull; Données privées &bull; RGPD
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 6. NOTRE VISION */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-menioo-cream to-white rounded-3xl p-10 md:p-14 border border-gray-100 shadow-sm">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-menioo-green/10 flex items-center justify-center mr-4">
                  <span className="text-2xl">✨</span>
                </div>
                <h2 className="text-3xl font-display font-bold text-menioo-navy">Notre vision</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>
                  Menioo est né d'une conviction simple : <strong>la cuisine devrait être un plaisir, pas une corvée</strong>.
                </p>
                <p>
                  Nous croyons que tout le monde peut cuisiner — même ceux qui pensent ne pas en être capables. Il suffit d'avoir les bons outils et la bonne organisation.
                </p>
                <p>
                  Notre ambition : devenir le compagnon culinaire qui vous accompagne du moment où une recette vous fait envie — dans la communauté, sur les réseaux ou dans un livre — jusqu'au moment où vous la savourez. De l'inspiration à l'assiette, sans friction.
                </p>
              </div>
              
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-lg font-medium text-menioo-navy italic flex items-start">
                  <span className="text-3xl text-menioo-green mr-3 -mt-2">"</span>
                  Le meilleur repas, c'est celui que vous cuisinez vous-même. On est là pour que ça arrive plus souvent.
                </p>
                <p className="text-sm text-gray-500 mt-3 ml-8">— L'équipe Menioo</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. CTA FINAL */}
      <section className="py-20 bg-menioo-navy text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-menioo-green/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-menioo-orange/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="container mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-6">
            Prêt à rejoindre{' '}
            <span className="text-menioo-orange">la communauté</span> ?
          </h2>
          
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
            Gratuit. Sans carte bancaire. Votre premier repas planifié en moins de 2 minutes.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {ctaButton(
              'Commencer maintenant',
              'inline-flex items-center justify-center bg-menioo-orange text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-menioo-orange-dark transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5'
            )}
            <Link 
              to="/pricing"
              className="inline-flex items-center justify-center bg-transparent border-2 border-gray-500 text-gray-300 px-10 py-5 rounded-xl font-semibold text-xl hover:border-white hover:text-white transition-all"
            >
              Voir les tarifs
            </Link>
          </div>
          
          <p className="mt-6 text-gray-400 flex items-center justify-center">
            <Shield className="h-4 w-4 mr-2" />
            Inscription sécurisée &bull; Aucune carte requise
          </p>
        </div>
      </section>

      {/* 8. FOOTER */}
      <footer className="bg-menioo-navy text-white py-12 border-t border-gray-700">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-1">
              <div className="flex items-center mb-4">
                <ChefHat className="h-8 w-8 text-menioo-green mr-2" />
                <span className="text-2xl font-display font-bold">Menioo</span>
              </div>
              <p className="text-gray-400">
                De l'inspiration à l'assiette. Recettes de la communauté, de vos créateurs préférés ou les vôtres.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">Produit</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-white transition-colors">Accueil</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Tarifs</Link></li>
                <li><Link to="/why-menioo" className="hover:text-white transition-colors">Pourquoi Menioo</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="mailto:contact@menioo.app" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">Légal</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/terms" className="hover:text-white transition-colors">CGU</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Confidentialité</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400">&copy; {new Date().getFullYear()} Menioo. Tous droits réservés.</p>
            <p className="text-gray-400 mt-4 md:mt-0">
              Fait avec <Heart className="h-4 w-4 text-menioo-orange inline mx-1" /> par l'équipe Menioo
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default WhyMenioo;